// Typed writes for what the Story room changes, as the Swift writes them (each one Automerge change per document, with
// the app's commit message): story times accepted from suggestions (`DocumentStore.setStoryTimes`, after a "Before
// setting story times" checkpoint), entities (`putEntity` — the core's, reused — `deleteEntity`, `ensureEntityColors`,
// `LibraryModel.createCharacters(named:)`, `saveEntity` with `renameCharacterStudy`), presence
// (`FragmentDocument.setPresence` through the app's `updateFragment`), glossary entries (`putGlossaryEntry` through the
// settings-JSON rules), pronunciations (`setPronunciation`/`removePronunciation`, the app's `setSayAs`) and how people
// travel in the story (`setTravelSettings` through the app's `setTravel`).
//
// `StoryEditor` is the core's `StoreEditor` (so `putEntity`, `ensureCharacterStudies`, `createCheckpoint` and
// `setMetadata` are the ones the rest of the core uses) with these added.
import type { EntityKind } from '../contract.ts';
import type { StoryEntity } from '../model/documents.ts';
import { swiftCompare } from '../model/order.ts';
import { nextEntityColor } from '../colors.ts';
import { StoreEditor, ModelError, writeEntity } from '../edit/storeEditor.ts';
import { fragmentDocName, makeID } from '../edit/editProject.ts';
import { str, timestamp, type Writer } from '../edit/typed.ts';
import { ROOT, SwiftReader } from '../automerge/swiftDoc.ts';
import { StandardField } from '../project.ts';
import { decodeGlossaryEntry, glossaryEntry, glossaryID, readGlossary, readPronunciations, type GlossaryEntry, type GlossaryKind } from './glossary.ts';
import { entityNames, type PresenceLevel } from '../presence.ts';
import { putJSON } from '../settingsJSON.ts';
import { decodeTravelSettings, isPeriodTravel, readTravel, sameTravel, TRAVEL_KEY, type TravelSettings } from './travel.ts';
import { charIsUppercase as isUppercase } from '../text/scalars.ts';
import { graphemes, trimCFWhitespace } from '../unicode.ts';

/** A new entity, as `StoryEntity(kind:name:)` makes it. */
export function newEntity(kind: EntityKind, name: string, id: string = makeID()): StoryEntity {
  return { id, kind, name: trimCFWhitespace(name), aliases: [], notes: '' };
}

export class StoryEditor extends StoreEditor {
  // MARK: Story times

  /** `DocumentStore.setStoryTimes`: the accepted story times (fragment id → the clock reading, as entered), after a
   *  "Before setting story times" checkpoint; each fragment committed "Set story time". Returns how many were set
   *  (fragments not here, e.g. not synced yet, are skipped). */
  setStoryTimes(times: Map<string, Date> | Record<string, Date>): number {
    const entries = times instanceof Map ? [...times] : Object.entries(times);
    if (!entries.length) return 0;
    this.createCheckpoint('Before setting story times');
    const at = this.now();
    let changed = 0;
    for (const [id, time] of entries) {
      if (!this.docs.has(fragmentDocName(id))) continue;
      this.change(fragmentDocName(id), 'Set story time', at, (w) => {
        w.putIfChanged(['metadata'], StandardField.storyTime, timestamp(time));
        w.put([], 'modifiedAt', timestamp(at));
      });
      changed += 1;
    }
    return changed;
  }

  // MARK: Presence

  /** `setPresence`: sets (or, with null, clears back to inferred) an entity's presence in a fragment; the app's
   *  `updateFragment` stamps `modifiedAt` and commits without a message. */
  setPresence(fragmentID: string, entityID: string, level: PresenceLevel | null): string[] {
    return this.updateFragment(fragmentID, (w) => {
      if (level !== null) w.putIfChanged(['presence'], entityID, str(level));
      else w.delete(['presence'], entityID);
    });
  }

  // MARK: Entities

  /** `ensureEntityColors`: every entity stored before colours existed gets its colour, deterministically (by kind, name,
   *  id), so two devices doing it before they sync choose the same. Committed with the app's "Add standard fields". */
  ensureEntityColors(message = 'Add standard fields'): boolean {
    const all = [...this.project().entities].sort((a, b) =>
      swiftCompare(a.kind, b.kind) || swiftCompare(a.name.toLocaleLowerCase(), b.name.toLocaleLowerCase()) || swiftCompare(a.id, b.id));
    if (!all.some((e) => e.color === undefined)) return false;
    return this.change('project', message, this.now(), (w) => {
      for (let i = 0; i < all.length; i++) {
        if (all[i].color !== undefined) continue;
        const color = nextEntityColor(all[i].kind, all);
        all[i] = { ...all[i], color };
        w.putIfChanged(['entities', all[i].id], 'color', str(color));
      }
    });
  }

  /** `deleteEntity`, committed "Delete <name>". */
  deleteEntity(id: string): string[] {
    const entity = this.project().entities.find((e) => e.id === id);
    if (!entity) return [];
    return this.change('project', `Delete ${entity.name}`, this.now(), (w) => w.delete(['entities'], id)) ? ['project'] : [];
  }

  /** The app's `saveEntity`: an edit to an entity the project has (or, `creating`, a new one), committed "Update
   *  <name>"; a renamed character's study page follows while its title is still the old name. */
  saveEntity(entity: StoryEntity, creating = false): string[] {
    const previous = this.project().entities.find((e) => e.id === entity.id);
    if (!previous && !creating) return [];
    const changed = this.putEntity(entity, `Update ${entity.name}`);
    if (previous && previous.name !== entity.name) {
      const current = this.project().entities.find((e) => e.id === entity.id);
      if (current) for (const n of this.renameCharacterStudy(current, previous.name)) if (!changed.includes(n)) changed.push(n);
    }
    return changed;
  }

  /** `createEntity(named:kind:)`: a new entity (e.g. from an @mention of a new name). */
  createEntity(name: string, kind: EntityKind): StoryEntity {
    const entity = newEntity(kind, name, this.id());
    this.saveEntity(entity, true);
    return this.project().entities.find((e) => e.id === entity.id) ?? entity;
  }

  /** `DocumentStore.renameCharacterStudy`: keeps a study page's title in step with its character, unless the writer
   *  has retitled it; committed "Rename study page". */
  renameCharacterStudy(character: StoryEntity, oldName: string): string[] {
    const id = character.studyID;
    if (id === undefined || oldName === character.name || !this.docs.has(fragmentDocName(id))) return [];
    if (new SwiftReader(this.fragmentDoc(id)).string(ROOT, 'title') !== oldName) return [];
    return this.change(fragmentDocName(id), 'Rename study page', this.now(), (w) => w.putIfChanged([], 'title', str(character.name)))
      ? [fragmentDocName(id)] : [];
  }

  /** `LibraryModel.createCharacters(named:)`: a character for each name not already in the cast (names and aliases,
   *  ignoring case; repeats once), in one change ("Add <name>" / "Add <n> characters"), each taking the least-used
   *  colour; then (as the app's reload does) their study pages. Returns them as stored. */
  createCharacters(names: readonly string[], options: { studies?: boolean } = {}): StoryEntity[] {
    const project = this.project();
    const existing = new Set(project.entities.filter((e) => e.kind === 'character').flatMap(entityNames).map((n) => n.toLowerCase()));
    const seen = new Set<string>();
    const fresh = names.map(trimCFWhitespace).filter((n) => {
      const key = n.toLowerCase();
      if (n === '' || existing.has(key) || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    if (!fresh.length) return [];
    const entities = fresh.map((name) => newEntity('character', name, this.id()));
    const all = [...project.entities];
    this.change('project', entities.length === 1 ? `Add ${entities[0].name}` : `Add ${entities.length} characters`, this.now(), (w) => {
      for (const e of entities) {
        const color = nextEntityColor(e.kind, all);
        writeEntity(w, e, color);
        all.push({ ...e, color });
      }
    });
    if (options.studies ?? true) this.ensureCharacterStudies();
    const ids = new Set(entities.map((e) => e.id));
    return this.project().entities.filter((e) => ids.has(e.id));
  }

  // MARK: Glossary

  /** `saveGlossaryEntry`: stores an entry (a renamed term, which has a new id, replaces `replacing`), committed
   *  "Glossary: <term>". Throws `ModelError` (changing nothing) when the stored entry for the term can't be read here. */
  saveGlossaryEntry(entry: GlossaryEntry, replacing?: GlossaryEntry): string[] {
    if (entry.term === '') return [];
    return this.change('project', `Glossary: ${entry.term}`, this.now(), (w) => {
      if (replacing && replacing.id !== entry.id) deleteGlossaryKey(w, replacing.id);
      putJSON(w, ['settings', 'glossary'], entry.id, entry, decodeGlossaryEntry, `glossary entry for “${entry.term}”`);
    }) ? ['project'] : [];
  }

  /** `deleteGlossaryEntry`, committed "Glossary: remove <term>". */
  deleteGlossaryEntry(entry: Pick<GlossaryEntry, 'id' | 'term'>): string[] {
    return this.change('project', `Glossary: remove ${entry.term}`, this.now(), (w) => deleteGlossaryKey(w, entry.id)) ? ['project'] : [];
  }

  /** `addToGlossary`: a selected word or name joins the glossary (a name if it's capitalised, else a word), unless it's
   *  there already. */
  addToGlossary(word: string): string[] {
    if (readGlossary(this.project()).some((e) => e.id === glossaryID(word))) return [];
    const first = graphemes(word)[0];
    const kind: GlossaryKind = first !== undefined && isUppercase(first) ? 'name' : 'word';
    return this.saveGlossaryEntry(glossaryEntry(word, kind));
  }

  // MARK: Travel

  /** The app's `setTravel` (`ProjectDocument.setTravelSettings`): how people travel in the story, as JSON at
   *  `settings.travel` through the settings-JSON rules — today's transport stores nothing — committed "Travel in the
   *  story", unless it's what's stored already. Throws `ModelError` (changing nothing) when the stored value can't be read
   *  here. */
  setTravel(travel: TravelSettings): string[] {
    if (sameTravel(travel, readTravel(this.project().settings))) return [];
    return this.change('project', 'Travel in the story', this.now(), (w) => {
      w.ensure(['settings']);
      putJSON(w, ['settings'], TRAVEL_KEY, isPeriodTravel(travel) ? travel : null, decodeTravelSettings, 'travel settings');
    }) ? ['project'] : [];
  }

  // MARK: Pronunciations

  /** `setPronunciation(_:for:)` (an empty `spoken` removes it), committed with `message`. */
  setPronunciation(spoken: string, written: string, message = `Pronunciation: ${written}`): string[] {
    if (spoken === '') return this.removePronunciation(written, message);
    return this.change('project', message, this.now(), (w) => w.putIfChanged(['settings', 'pronunciations'], written, str(spoken))) ? ['project'] : [];
  }

  /** `removePronunciation(for:)`. */
  removePronunciation(written: string, message = `Pronunciation: ${written}`): string[] {
    if (!(written in readPronunciations(this.project()))) return [];
    return this.change('project', message, this.now(), (w) => w.delete(['settings', 'pronunciations'], written)) ? ['project'] : [];
  }

  /** The app's `setSayAs`: how one term is said (empty removes it), under the key already stored for it ignoring case,
   *  so pronunciations another device added are never touched. Committed "Pronunciation: <key>". */
  setSayAs(term: string, spoken: string): string[] {
    const value = trimCFWhitespace(spoken);
    const stored = readPronunciations(this.project());
    const key = Object.keys(stored).find((k) => k.toLowerCase() === term.toLowerCase()) ?? term;
    if ((stored[key] ?? null) === (value === '' ? null : value)) return [];
    return value === '' ? this.removePronunciation(key) : this.setPronunciation(value, key);
  }
}

function deleteGlossaryKey(w: Writer, id: string) {
  w.delete(['settings', 'glossary'], id);
}

export { ModelError };
